import React, { useState, useEffect, useCallback } from "react";
import { get } from "../../services/api";

function VisitsReports({ qaUsers, centers }) { // Removed 'schedules' and 'onDownloadReport' as we handle them locally now
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [filters, setFilters] = useState({
    user: "ALL",
    status: "ALL",
    fromDate: "",
    toDate: "",
  });

  const [filteredVisits, setFilteredVisits] = useState([]);

  // Fetch Visits Data
  const fetchVisits = useCallback(async () => {
    setLoading(true);
    try {
      const response = await get("QAVisit/GetQAVisitByUserId");
      // The response is expected to be an array as per the user request
      const data = Array.isArray(response) ? response : [];
      setVisits(data);
      setFilteredVisits(data);
    } catch (error) {
      console.error("Error fetching QA visits:", error);
      // setVisits([]); // Keep empty on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  // Apply filters
  useEffect(() => {
    let filtered = [...visits];

    // Filter by QA User (using name match since API returns name, or id if available)
    // The new API returns "name" and "id" (which might be visit ID or user ID ?? Request says "id": 6, "name": "Craig Vaughan", "status": ... ) 
    // Wait, the API response shows "id" is likely the Visit ID (6, 8, 7). 
    // "name" is the User Name.
    // The "qaUsers" prop is still passed from parent, we can try to filter by ID if we can map it, 
    // but the API response doesn't explicitly give "qaUserId". It gives "name".
    // However, the user provided sample response doesn't have "qaUserId". It has "name".
    // So filtering by "QA User" dropdown (which uses IDs) might be tricky unless we match by Name or if "id" in dropdown matches something.
    // Let's look closely at API Response:
    // { "id": 6, "name": "Craig Vaughan", ... } -> "id" 6 seems like VisitID.
    // For the filter to work with `qaUsers` (which has IDs), we ideally need `qaUserId` in the response.
    // If not available, we might have to filter by Name text or disable the filter.
    // BUT the sample response is just a snippet. 
    // Let's assume for now we filter by Name if "qaUserId" is missing.
    if (filters.user !== "ALL") {
       // Find the selected user's name from qaUsers array
       const selectedUser = qaUsers.find(u => String(u.id) === String(filters.user));
       if (selectedUser) {
         const fullName = `${selectedUser.firstName} ${selectedUser.lastName}`;
         filtered = filtered.filter(v => v.name === fullName || v.name.includes(selectedUser.firstName));
       }
    }

    // Filter by Status
    if (filters.status !== "ALL") {
      filtered = filtered.filter(s => s.status === filters.status);
    }

    // Filter by Date Range
    if (filters.fromDate) {
      filtered = filtered.filter(s => {
        const visitDate = new Date(s.visitDate);
        const fromDate = new Date(filters.fromDate);
        return visitDate >= fromDate;
      });
    }

    if (filters.toDate) {
      filtered = filtered.filter(s => {
        const visitDate = new Date(s.visitDate);
        const toDate = new Date(filters.toDate);
        return visitDate <= toDate;
      });
    }

    setFilteredVisits(filtered);
  }, [visits, filters, qaUsers]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      user: "ALL",
      status: "ALL",
      fromDate: "",
      toDate: "",
    });
  };

  const getStatusBadge = (status) => {
    const baseStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.3px',
      textTransform: 'uppercase'
    };

    const styles = {
      Pending: {
        background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
        color: '#856404',
        border: '1px solid #ffd166'
      },
      "In Progress": {
        background: 'linear-gradient(135deg, #cfe2ff 0%, #a5c8ff 100%)',
        color: '#084298',
        border: '1px solid #74c0fc'
      },
      Completed: {
        background: 'linear-gradient(135deg, #d4edda 0%, #a8e6a3 100%)',
        color: '#155724',
        border: '1px solid #8fd19e'
      },
      Submitted: { // Added Submitted as per new API response status
        background: 'linear-gradient(135deg, #d1e7dd 0%, #a3cfbb 100%)',
        color: '#0f5132',
        border: '1px solid #75b798'
      },
      Missed: {
        background: 'linear-gradient(135deg, #f8d7da 0%, #ffb8b8 100%)',
        color: '#842029',
        border: '1px solid #ff9b9b'
      }
    };

    const style = styles[status] || {
      background: '#f3f5f7',
      color: '#6c757d',
      border: '1px solid #e2e8f0'
    };

    return (
      <span style={{ ...baseStyle, ...style }}>
        {status === "Pending" && <i className="fas fa-clock" style={{ fontSize: '0.65rem' }}></i>}
        {status === "In Progress" && <i className="fas fa-play-circle" style={{ fontSize: '0.65rem' }}></i>}
        {(status === "Completed" || status === "Submitted") && <i className="fas fa-check-circle" style={{ fontSize: '0.65rem' }}></i>}
        {status === "Missed" && <i className="fas fa-times-circle" style={{ fontSize: '0.65rem' }}></i>}
        {status}
      </span>
    );
  };

  const handleReportClick = (reportPath) => {
    if (reportPath) {
      // User requested format: https://api.inspirertechnologies.com/ExternalImages///Student/...
      // This implies we need to concatenate base URL + reportPath. 
      // Assuming reportPath starts with slash, and base needs double slash at end or one in base and one in path + one extra?
      // Based on user sample: https://api.inspirertechnologies.com/ExternalImages///Student/...
      
      const baseUrl = "https://api.inspirertechnologies.com/ExternalImages/";
      // If reportPath is "/Student...", result is ...ExternalImages//Student... (double)
      // Triple slashes usually just resolve to one or two. I will concatenate simply.
      
      const fullPath = "https://api.inspirertechnologies.com/ExternalImages/" + reportPath;
      window.open(fullPath, '_blank');
    }
  };

  return (
    <div className="row g-3">
      {/* Left Column - Filters & Legend */}
      <div className="col-lg-3">
        {/* Filters Card */}
        <div className="form-section" style={{ 
          border: '1px solid #e0e6ed',
          borderRadius: '12px',
          padding: '1.25rem 1.5rem',
          background: 'white',
          marginBottom: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          <div className="form-section-title" style={{ 
            fontWeight: 700,
            fontSize: '1rem',
            marginBottom: '1rem',
            color: '#2c3e50',
            borderBottom: '2px solid #f1f5f9',
            paddingBottom: '0.75rem'
          }}>
            <i className="fas fa-filter mr-2" style={{ color: '#4f46e5' }}></i>
            Filters
          </div>
          
          <div className="mb-3">
            <label className="form-label" style={{ 
              marginBottom: '0.5rem',
              fontWeight: 500,
              lineHeight: 1.2,
              fontSize: '0.85rem',
              color: '#4a5568'
            }}>
              QA User
            </label>
            <select
              value={filters.user}
              onChange={(e) => handleFilterChange("user", e.target.value)}
              className="form-select form-select-sm"
              style={{
                display: 'block',
                width: '100%',
                padding: '0.5rem 0.75rem',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: '#2d3748',
                backgroundColor: '#fff',
                backgroundClip: 'padding-box',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                transition: 'all 0.2s ease'
              }}
            >
              <option value="ALL">All QA Users</option>
              {qaUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-3">
            <label className="form-label" style={{ 
              marginBottom: '0.5rem',
              fontWeight: 500,
              lineHeight: 1.2,
              fontSize: '0.85rem',
              color: '#4a5568'
            }}>
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="form-select form-select-sm"
              style={{
                display: 'block',
                width: '100%',
                padding: '0.5rem 0.75rem',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: '#2d3748',
                backgroundColor: '#fff',
                backgroundClip: 'padding-box',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                transition: 'all 0.2s ease'
              }}
            >
              <option value="ALL">All Status</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Submitted">Submitted</option>
              <option value="Missed">Missed</option>
            </select>
          </div>

          <div className="mb-3">
            <label className="form-label" style={{ 
              marginBottom: '0.5rem',
              fontWeight: 500,
              lineHeight: 1.2,
              fontSize: '0.85rem',
              color: '#4a5568'
            }}>
              From Date
            </label>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange("fromDate", e.target.value)}
              className="form-control form-control-sm"
              style={{
                display: 'block',
                width: '100%',
                padding: '0.5rem 0.75rem',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: '#2d3748',
                backgroundColor: '#fff',
                backgroundClip: 'padding-box',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                transition: 'all 0.2s ease'
              }}
            />
          </div>

          <div className="mb-3">
            <label className="form-label" style={{ 
              marginBottom: '0.5rem',
              fontWeight: 500,
              lineHeight: 1.2,
              fontSize: '0.85rem',
              color: '#4a5568'
            }}>
              To Date
            </label>
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => handleFilterChange("toDate", e.target.value)}
              className="form-control form-control-sm"
              style={{
                display: 'block',
                width: '100%',
                padding: '0.5rem 0.75rem',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: '#2d3748',
                backgroundColor: '#fff',
                backgroundClip: 'padding-box',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                transition: 'all 0.2s ease'
              }}
            />
          </div>

          <button
            onClick={resetFilters}
            className="btn btn-outline-gray btn-sm w-100"
            style={{
              border: '1px solid #e2e8f0',
              color: '#64748b',
              fontWeight: 500,
              padding: '0.5rem',
              borderRadius: '6px',
              fontSize: '0.8125rem',
              marginTop: '0.5rem',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f1f5f9';
              e.currentTarget.style.borderColor = '#cbd5e0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = '#e2e8f0';
            }}
          >
            <i className="fas fa-redo-alt mr-2" style={{ fontSize: '0.75rem' }}></i>
            Reset Filters
          </button>
        </div>

        {/* Legend Card - Simplified */}
        <div className="form-section" style={{ 
          border: '1px solid #e0e6ed',
          borderRadius: '12px',
          padding: '1.25rem 1.5rem',
          background: 'white',
          marginBottom: '1rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
           <div className="form-section-title" style={{ 
            fontWeight: 700,
            fontSize: '1rem',
            marginBottom: '1rem',
            color: '#2c3e50',
            borderBottom: '2px solid #f1f5f9',
            paddingBottom: '0.75rem'
          }}>
            <i className="fas fa-info-circle mr-2" style={{ color: '#4f46e5' }}></i>
            Status Legend
          </div>
           <div className="d-flex flex-column gap-2" style={{ fontSize: '0.875rem' }}>
             {['Pending', 'In Progress', 'Submitted', 'Missed'].map(status => (
                <div key={status} className="d-flex align-items-center gap-2">
                 {getStatusBadge(status)}
                </div>
             ))}
           </div>
        </div>
      </div>

      {/* Right Column - Visits Table */}
      <div className="col-lg-9">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 className="mb-0" style={{ fontSize: '1rem', fontWeight: 700, color: '#2c3e50' }}>
              <i className="fas fa-clipboard-list mr-2" style={{ color: '#4f46e5' }}></i>
                All QA Visits ({filteredVisits.length})
            </h6>
            <div className="small-helper mt-1" style={{ fontSize: '0.75rem', color: '#718096' }}>
              Showing {filteredVisits.length} of {visits.length} visits
            </div>
          </div>
          
          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
             {loading && <span className="text-primary"><i className="fas fa-spinner fa-spin mr-1"></i> Refreshing...</span>}
          </div>
        </div>

        <div className="table-responsive" style={{
          border: '1px solid #e0e6ed',
          borderRadius: '10px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          <table className="table table-sm align-middle mb-0">
            <thead style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderBottom: '2px solid #e2e8f0'
            }}>
              <tr>
                <th style={{ width: '40px', padding: '0.875rem 1rem', fontSize: '0.7rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>#</th>
                <th style={{ padding: '0.875rem 1rem', fontSize: '0.7rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Visit ID</th>
                <th style={{ padding: '0.875rem 1rem', fontSize: '0.7rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>QA User</th>
                <th style={{ padding: '0.875rem 1rem', fontSize: '0.7rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Center / City</th>
                <th style={{ padding: '0.875rem 1rem', fontSize: '0.7rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Visit Date</th>
                <th style={{ padding: '0.875rem 1rem', fontSize: '0.7rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Priority</th>
                <th style={{ padding: '0.875rem 1rem', fontSize: '0.7rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Status</th>
                <th style={{ padding: '0.875rem 1rem', fontSize: '0.7rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', textAlign: 'center' }}>Report</th>
              </tr>
            </thead>
            
            <tbody>
              {loading && filteredVisits.length === 0 ? (
                 <tr><td colSpan="8" className="text-center py-4">Loading visits...</td></tr>
              ) : filteredVisits.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center text-muted py-5" style={{ background: '#f8fafc' }}>
                    <div className="py-3">
                      <i className="fas fa-search fa-2x mb-3" style={{ color: '#cbd5e0' }}></i>
                      <div>No visits found</div>
                      <div className="small mt-2" style={{ fontSize: '0.75rem' }}>Try adjusting your filters</div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredVisits.map((visit, index) => (
                  <tr key={visit.id || index} style={{
                    borderBottom: '1px solid #f1f5f9',
                    background: index % 2 === 0 ? '#fff' : '#fafcff'
                  }}>
                    <td style={{ padding: '0.875rem 1rem', fontSize: '0.8125rem', color: '#64748b' }}>
                      {index + 1}
                    </td>
                    <td style={{ padding: '0.875rem 1rem', fontSize: '0.8125rem', fontWeight: 600, color: '#4f46e5' }}>
                      {visit.id}
                    </td>
                    <td style={{ padding: '0.875rem 1rem', fontSize: '0.8125rem', fontWeight: 500, color: '#2d3748' }}>
                      {/* Using Name directly from API */}
                      <div className="d-flex align-items-center">
                        <div style={{
                          width: '24px', height: '24px', borderRadius: '50%', background: '#e2e8f0', 
                          display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '8px',
                          fontSize: '0.7rem', fontWeight: 'bold', color: '#4a5568'
                        }}>
                          {visit.name ? visit.name.charAt(0) : '?'}
                        </div>
                        {visit.name}
                      </div>
                    </td>
                    <td style={{ padding: '0.875rem 1rem' }}>
                      <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#2d3748' }}>{visit.centerName}</div>
                      <div style={{ fontSize: '0.75rem', color: '#718096' }}>{visit.city}</div>
                    </td>
                    <td style={{ padding: '0.875rem 1rem', fontSize: '0.8125rem', color: '#64748b' }}>
                      {visit.visitDate ? new Date(visit.visitDate).toLocaleDateString() : '-'}
                    </td>
                    <td style={{ padding: '0.875rem 1rem' }}>
                       {visit.priority === 'High' ? (
                         <span className="badge bg-danger bg-opacity-10 text-danger rounded-pill px-2">High</span>
                       ) : visit.priority === 'Medium' ? (
                          <span className="badge bg-warning bg-opacity-10 text-warning rounded-pill px-2">Medium</span>
                       ) : (
                          <span className="badge bg-info bg-opacity-10 text-info rounded-pill px-2">Low</span>
                       )}
                    </td>
                    <td style={{ padding: '0.875rem 1rem' }}>
                      {getStatusBadge(visit.status)}
                    </td>
                    <td style={{ padding: '0.875rem 1rem', textAlign: 'center' }}>
                      {visit.reportPath ? (
                        <button
                          onClick={() => handleReportClick(visit.reportPath)}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            padding: '4px',
                            transition: 'transform 0.2s'
                          }}
                          title="View Report"
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                           {/* External Image for Report Button */}
                           <img 
                              src="https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg" 
                              alt="PDF Report" 
                              style={{ width: '28px', height: '28px' }}
                           />
                        </button>
                      ) : (
                        <span className="text-muted" style={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
                          No Report
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default VisitsReports;